import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Trophy, Star, Award, Gift, TrendingUp, Plus, Medal, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  performance: { label: 'Performance', icon: TrendingUp, color: 'text-[#00A6A0]' },
  attendance: { label: 'Attendance', icon: Star, color: 'text-amber-500' },
  training: { label: 'Training', icon: Award, color: 'text-blue-500' },
  client_feedback: { label: 'Client Feedback', icon: Gift, color: 'text-purple-500' },
  teamwork: { label: 'Teamwork', icon: Medal, color: 'text-emerald-500' },
};

export default function RewardsAdmin() {
  const [data, setData] = useState({ rewards: [], leaderboard: [], total_points_awarded: 0 });
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAward, setShowAward] = useState(false);
  const [awarding, setAwarding] = useState(false);
  const [form, setForm] = useState({ caregiver_id: '', points: 10, reason: '', category: 'performance' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rewardsRes, cgRes] = await Promise.all([
        axios.get(`${API_URL}/api/rewards/admin`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/caregivers`, { withCredentials: true })
      ]);
      setData(rewardsRes.data);
      setCaregivers(cgRes.data);
    } catch { toast.error('Failed to load rewards data'); }
    setLoading(false);
  };

  const handleAward = async (e) => {
    e.preventDefault();
    if (!form.caregiver_id || !form.reason) { toast.error('Select a caregiver and provide a reason'); return; }
    setAwarding(true);
    try {
      await axios.post(`${API_URL}/api/rewards/award`, form, { withCredentials: true });
      toast.success('Reward awarded!');
      setShowAward(false);
      setForm({ caregiver_id: '', points: 10, reason: '', category: 'performance' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to award'); }
    setAwarding(false);
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex justify-center py-20"><div className="spinner w-8 h-8" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="rewards-admin-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Rewards Portal</h1>
            <p className="text-muted-foreground mt-1">Recognize and reward caregiver excellence</p>
          </div>
          <Dialog open={showAward} onOpenChange={setShowAward}>
            <DialogTrigger asChild>
              <Button className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" data-testid="award-points-btn">
                <Plus className="w-4 h-4 mr-2" /> Award Points
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md glass-card border-border">
              <DialogHeader>
                <DialogTitle className="text-foreground">Award Reward Points</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAward} className="space-y-4">
                <div>
                  <Label className="text-foreground">Caregiver *</Label>
                  <Select value={form.caregiver_id} onValueChange={v => setForm(f => ({ ...f, caregiver_id: v }))}>
                    <SelectTrigger className="mt-1 bg-muted border-border text-foreground" data-testid="reward-caregiver-select">
                      <SelectValue placeholder="Select caregiver" />
                    </SelectTrigger>
                    <SelectContent>
                      {caregivers.map(cg => (
                        <SelectItem key={cg.id} value={cg.id}>{cg.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-foreground">Points *</Label>
                    <Input type="number" min="1" value={form.points} onChange={e => setForm(f => ({ ...f, points: parseInt(e.target.value) || 0 }))} className="mt-1 bg-muted border-border text-foreground" data-testid="reward-points-input" />
                  </div>
                  <div>
                    <Label className="text-foreground">Category</Label>
                    <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                      <SelectTrigger className="mt-1 bg-muted border-border text-foreground" data-testid="reward-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="performance">Performance</SelectItem>
                        <SelectItem value="attendance">Attendance</SelectItem>
                        <SelectItem value="training">Training</SelectItem>
                        <SelectItem value="client_feedback">Client Feedback</SelectItem>
                        <SelectItem value="teamwork">Teamwork</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-foreground">Reason *</Label>
                  <textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} className="mt-1 w-full rounded-md bg-muted border border-border text-foreground p-3 text-sm min-h-[80px] resize-none" placeholder="Why are they being rewarded?" data-testid="reward-reason-input" />
                </div>
                <Button type="submit" className="w-full bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" disabled={awarding} data-testid="submit-award-btn">
                  {awarding ? 'Awarding...' : 'Award Points'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="glass-card border-border" data-testid="total-points-stat">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-[#00A6A0]/10">
                <Trophy className="w-6 h-6 text-[#00A6A0]" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Points Awarded</p>
                <p className="text-2xl font-bold text-foreground">{data.total_points_awarded.toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card border-border" data-testid="total-rewards-stat">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-500/10">
                <Star className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Rewards Given</p>
                <p className="text-2xl font-bold text-foreground">{data.rewards.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card border-border" data-testid="top-performer-stat">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-purple-500/10">
                <Crown className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Top Performer</p>
                <p className="text-lg font-bold text-foreground truncate">{data.leaderboard[0]?.name || 'N/A'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Leaderboard */}
          <Card className="glass-card border-border lg:col-span-1" data-testid="leaderboard-card">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#00A6A0]" />
                Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.leaderboard.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No points awarded yet</p>
              ) : (
                <div className="space-y-2">
                  {data.leaderboard.map((entry, i) => (
                    <div key={entry.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30" data-testid={`leaderboard-entry-${i}`}>
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-amber-500 text-white' : i === 1 ? 'bg-gray-400 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-muted text-muted-foreground'
                      }`}>
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{entry.name}</p>
                      </div>
                      <span className="text-sm font-bold text-[#00A6A0]">{entry.points} pts</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Rewards */}
          <Card className="glass-card border-border lg:col-span-2" data-testid="recent-rewards-card">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Recent Awards
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.rewards.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No rewards given yet</p>
              ) : (
                <div className="space-y-3">
                  {data.rewards.slice(0, 20).map((reward, i) => {
                    const catConf = CATEGORY_CONFIG[reward.category] || CATEGORY_CONFIG.performance;
                    const CatIcon = catConf.icon;
                    return (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/20 border border-border/50" data-testid={`reward-entry-${i}`}>
                        <CatIcon className={`w-5 h-5 mt-0.5 ${catConf.color}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-foreground">{reward.caregiver_name}</span>
                            <span className="text-xs text-[#00A6A0] font-bold">+{reward.points} pts</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{reward.reason}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            By {reward.awarded_by_name} · {reward.created_at ? new Date(reward.created_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">{catConf.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
